/* v-tip — a lightweight styled tooltip matching go.lacak.io.
 *
 * The deployed app renders tooltips with the popper-based `v-tooltip` library
 * (`.tooltip.vue-tooltip-theme`): a small box — background rgba(0,0,0,.7), white 14px/400,
 * padding 6px 12px, border-radius 4px, NO shadow — placed below the activator with a little
 * arrow and shown after a short hover delay. We reproduce that here without pulling in popper:
 * one SHARED node is portalled to <body> (so a panel/toolbar `overflow:hidden` can never clip
 * it), positioned from getBoundingClientRect() and clamped to the viewport, with the arrow
 * kept pointing at the activator's centre even after clamping.
 *
 *   v-tip="'Search'"            → default placement (bottom)
 *   v-tip.left="'Layers'"       → placement via modifier: .top .bottom .left .right
 *   v-tip="someExpression"      → dynamic text (re-read on update)
 *
 * The value is plain text (not HTML) — assigned via textContent, so it is injection-safe.
 */

const OPEN_DELAY = 350 // ms hover before showing (matches the real app's un-fussy delay)
const GAP = 8 // px between activator edge and tooltip
const MARGIN = 4 // px min distance from the viewport edge when clamping

let node = null // the single shared tooltip element (lazily created)
let inner = null
let arrow = null
let current = null // the activator element the tooltip is currently bound to
let showTimer = null
let timerEl = null // which element owns the pending open-timer (so we only cancel our own)

function injectStyleOnce() {
  if (document.getElementById('app-tip-style')) return
  const s = document.createElement('style')
  s.id = 'app-tip-style'
  s.textContent = [
    '.app-tip{position:fixed;top:0;left:0;z-index:4000;pointer-events:none;opacity:0;',
    'transition:opacity .12s ease;will-change:transform}',
    '.app-tip.app-tip--show{opacity:1}',
    '.app-tip__inner{background:rgba(0,0,0,.7);color:#fff;font:400 14px/1.2 Roboto,Arial,sans-serif;',
    'padding:6px 12px;border-radius:4px;white-space:nowrap;max-width:280px;',
    'overflow:hidden;text-overflow:ellipsis}',
    '.app-tip__arrow{position:absolute;width:0;height:0;border:5px solid transparent}',
    '.app-tip[data-tip-place="bottom"] .app-tip__arrow{top:-10px;border-bottom-color:rgba(0,0,0,.7);border-top:0}',
    '.app-tip[data-tip-place="top"] .app-tip__arrow{bottom:-10px;border-top-color:rgba(0,0,0,.7);border-bottom:0}',
    '.app-tip[data-tip-place="left"] .app-tip__arrow{right:-10px;border-left-color:rgba(0,0,0,.7);border-right:0}',
    '.app-tip[data-tip-place="right"] .app-tip__arrow{left:-10px;border-right-color:rgba(0,0,0,.7);border-left:0}',
  ].join('')
  document.head.appendChild(s)
}

function ensureNode() {
  if (node) return
  injectStyleOnce()
  node = document.createElement('div')
  node.className = 'app-tip'
  node.setAttribute('role', 'tooltip')
  arrow = document.createElement('div')
  arrow.className = 'app-tip__arrow'
  inner = document.createElement('div')
  inner.className = 'app-tip__inner'
  node.appendChild(arrow)
  node.appendChild(inner)
  document.body.appendChild(node)
}

function placementOf(binding) {
  if (binding.modifiers.top) return 'top'
  if (binding.modifiers.left) return 'left'
  if (binding.modifiers.right) return 'right'
  return 'bottom'
}

function position(el, place) {
  const r = el.getBoundingClientRect()
  const nr = node.getBoundingClientRect() // measured after text set (opacity 0, still has size)
  const vw = window.innerWidth
  const vh = window.innerHeight
  const cx = r.left + r.width / 2
  const cy = r.top + r.height / 2

  // Auto-flip to the opposite side when there isn't room on the requested side.
  if (place === 'bottom' && r.bottom + GAP + nr.height > vh) place = 'top'
  else if (place === 'top' && r.top - GAP - nr.height < 0) place = 'bottom'
  else if (place === 'right' && r.right + GAP + nr.width > vw) place = 'left'
  else if (place === 'left' && r.left - GAP - nr.width < 0) place = 'right'

  let top
  let left
  if (place === 'bottom') { top = r.bottom + GAP; left = cx - nr.width / 2 }
  else if (place === 'top') { top = r.top - GAP - nr.height; left = cx - nr.width / 2 }
  else if (place === 'right') { left = r.right + GAP; top = cy - nr.height / 2 }
  else { left = r.left - GAP - nr.width; top = cy - nr.height / 2 } // left

  // Clamp into the viewport.
  left = Math.max(MARGIN, Math.min(left, vw - nr.width - MARGIN))
  top = Math.max(MARGIN, Math.min(top, vh - nr.height - MARGIN))

  node.style.transform = 'translate(' + Math.round(left) + 'px,' + Math.round(top) + 'px)'
  node.setAttribute('data-tip-place', place)

  // Keep the arrow pointing at the activator's centre even after the box was clamped.
  if (place === 'bottom' || place === 'top') {
    let ax = cx - left - 5 // arrow is 10px wide
    ax = Math.max(6, Math.min(ax, nr.width - 16))
    arrow.style.left = Math.round(ax) + 'px'
    arrow.style.top = ''
  } else {
    let ay = cy - top - 5
    ay = Math.max(6, Math.min(ay, nr.height - 16))
    arrow.style.top = Math.round(ay) + 'px'
    arrow.style.left = ''
  }
}

function show(el, text, place) {
  if (!text) return
  ensureNode()
  // Re-home the shared node into the active fullscreen element, if any. A node parented to
  // document.body sits OUTSIDE the fullscreen top-layer subtree, so the browser never paints it
  // while an element (e.g. the map) is fullscreened — map-control tooltips would silently vanish
  // (a regression from the native title= these replaced). Re-parenting per show() also restores
  // it to <body> once fullscreen exits. Fixed positioning keeps viewport coords correct either way.
  const host = (document.fullscreenElement && document.fullscreenElement.contains(el))
    ? document.fullscreenElement
    : document.body
  if (node.parentNode !== host) host.appendChild(node)
  current = el
  inner.textContent = text
  node.classList.remove('app-tip--show') // reset for a clean fade
  // position needs the node sized; it already is (display flow), measure then place
  position(el, place)
  // next frame → fade in
  node.classList.add('app-tip--show')
}

function hide(el) {
  if (!node) return
  if (el && current && el !== current) return // a newer activator already owns the tooltip
  node.classList.remove('app-tip--show')
  current = null
}

function clearTimer() {
  if (showTimer) { clearTimeout(showTimer); showTimer = null }
  timerEl = null
}

// Cancel the pending open-timer only if THIS element owns it. A single shared timer means an
// unrelated element unbinding/leaving must not cancel a still-hovered element's pending tooltip
// (op-chat-badge / op-balance churn continuously from the socket within the 350ms open-delay).
function clearTimerFor(el) {
  if (timerEl === el) clearTimer()
}

const tip = {
  bind(el, binding) {
    el.__tip = {
      text: binding.value == null ? '' : String(binding.value),
      place: placementOf(binding),
    }
    // Suppress any native title so we don't get a duplicate OS tooltip.
    if (el.getAttribute('title')) { el.__tip.nativeTitle = el.getAttribute('title'); el.removeAttribute('title') }

    el.__tipEnter = () => {
      clearTimer() // a new hover supersedes any other element's pending timer
      const t = el.__tip
      timerEl = el
      showTimer = setTimeout(() => { showTimer = null; timerEl = null; show(el, t.text, t.place) }, OPEN_DELAY)
    }
    el.__tipLeave = () => { clearTimerFor(el); hide(el) }
    // Hide immediately on click/scroll/blur so the tooltip never lingers over a changed UI.
    el.__tipGone = () => { clearTimerFor(el); hide(el) }

    el.addEventListener('mouseenter', el.__tipEnter)
    el.addEventListener('mouseleave', el.__tipLeave)
    el.addEventListener('mousedown', el.__tipGone)
    el.addEventListener('blur', el.__tipGone, true)
  },
  update(el, binding) {
    if (!el.__tip) return
    el.__tip.text = binding.value == null ? '' : String(binding.value)
    el.__tip.place = placementOf(binding)
    // If the tooltip is currently showing this element, refresh its text/position live.
    if (current === el && node && node.classList.contains('app-tip--show')) {
      inner.textContent = el.__tip.text
      position(el, el.__tip.place)
    }
  },
  unbind(el) {
    clearTimerFor(el) // only cancel the pending timer if THIS element owns it
    hide(el)
    if (el.__tipEnter) {
      el.removeEventListener('mouseenter', el.__tipEnter)
      el.removeEventListener('mouseleave', el.__tipLeave)
      el.removeEventListener('mousedown', el.__tipGone)
      el.removeEventListener('blur', el.__tipGone, true)
    }
    delete el.__tip
    delete el.__tipEnter
    delete el.__tipLeave
    delete el.__tipGone
  },
}

export default tip
